import React from 'react';
import {
  FormControl as ChakraFormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import './style.scss';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface IFormControlSelect {
  placeholder?: string;
  value?: string | number | null;
  form?: any;
  name: string;
  className?: string;
  onChange?: (value: string | number | null) => void;
  style?: React.CSSProperties;
  label?: string;
  options: SelectOption[];
  isRead?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  isSubmitted?: boolean;
  error?: string;
  leftIcon?: React.ReactNode;
}

const FormControlSelect: React.FC<IFormControlSelect> = (props) => {
  const { t } = useTranslation();
  const {
    placeholder,
    name,
    onChange,
    style,
    form,
    className,
    label,
    isRequired: explicitIsRequired,
    isSubmitted,
    error,
  } = props;

  const _form = form;
  
  // Determine current value: use props.value if passed, otherwise value from form
  const currentValue = props.value !== undefined ? props.value : _form?.values?.[name];

  // Determine if field is required: explicitly passed or from form
  const isRequired = explicitIsRequired !== undefined
    ? explicitIsRequired
    : (typeof form?.isRequired === 'function' ? form.isRequired(name) : false);

  // Get error message
  const errorMessage = error || _form?.errors?.[name];
  const isTouched = _form?.touched?.[name];
  const showError = !!errorMessage && (isSubmitted || isTouched);

  // Find selected option label for read mode
  const selectedOption = currentValue 
    ? props.options.find(opt => opt.value === currentValue || String(opt.value) === String(currentValue))
    : null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const parsedValue = value === '' ? null : value;
    
    if (_form?.setFieldValue) {
      _form.setFieldValue(name, parsedValue);
    }
    
    if (onChange) {
      onChange(parsedValue);
    }
  };

  return (
    <ChakraFormControl
      className={`form-control-select ${className || ''}`}
      style={style}
      isRequired={isRequired}
      isInvalid={showError}
    >
      {label && (
        <FormLabel htmlFor={name}>
          {t(label)}
        </FormLabel>
      )}

      {props.isRead ? (
        <Text>{selectedOption?.label || currentValue || '-'}</Text>
      ) : (
        <>
          <div className={props.leftIcon ? 'form-control-select__field' : undefined}>
            {props.leftIcon ? (
              <span className="form-control-select__icon">{props.leftIcon}</span>
            ) : null}
            <Select
              id={name}
              name={name}
              value={currentValue || ''}
              onChange={handleChange}
              placeholder={placeholder ? t(placeholder) : undefined}
              isDisabled={props.isDisabled}
              paddingLeft={props.leftIcon ? '54px' : undefined}
            >
              {props.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          {showError && (
            <FormErrorMessage>{errorMessage}</FormErrorMessage>
          )}
        </>
      )}
    </ChakraFormControl>
  );
};

export default FormControlSelect;

